<template>
    <v-dialog
        :value="isOpen"
        scrollable
        max-width="700px"
        transition="slide-y-transition"
        origin="top center"
        eager
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    <template v-if="isEditable && showOnly">
                        Towar - <strong>{{ productModel.name }}</strong>
                    </template>
                    <template v-else>
                        <template v-if="isNewProduct">
                            Dodaj towar
                        </template>
                        <template v-else>
                            Edycja towaru - <strong>{{ productModel.name }}</strong>
                        </template>
                    </template>
                </h1>
            </v-card-title>

            <div class="mx-5 my-1">
                <v-row>
                    <v-col cols="12">
                        <v-select
                            v-model="selectedProduct"
                            :items="formattedProducts"
                            label="Produkt"
                            return-object
                            outlined
                            dense
                            item-text="name"
                            :hint="isNewProduct && isProductAmountFullAssigned ? 'Produkt w pełni rozpisany.' : ''"
                            persistent-hint
                            :disabled="!isNewProduct || !isEditable || showOnly"
                            :error-messages="productErrors"
                            @change="changeProduct"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" sm="6">
                        <v-autocomplete
                            v-model="selectedPackage"
                            label="Opakowanie"
                            :items="packages"
                            item-text="name"
                            return-object
                            outlined
                            dense
                            :disabled="(isNewProduct && isProductAmountFullAssigned) || !isEditable || showOnly"
                            :error-messages="packagePackageTypeErrors"
                            @change="changePackageType"
                        />
                    </v-col>
                    <v-col cols="12" sm="6">
                        <v-text-field
                            v-model="productModel.goodUnitTargetInPackageSum"
                            v-mask="'#######'"
                            label="Nakład"
                            outlined
                            dense
                            persistent-hint
                            :hint="amountInfo"
                            :disabled="(isNewProduct && isProductAmountFullAssigned) || !isEditable || showOnly"
                            :error-messages="amountErrors"
                            @input="changeAmount"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" sm="4">
                        <v-text-field
                            v-model="productModel.goodUnitPackageLength"
                            label="Długość"
                            suffix="mm"
                            outlined
                            dense
                            :error-messages="packageLengthErrors"
                            :disabled="withoutSpecifiedCardbox || !isCustomMadeCardBoard || !isEditable || showOnly"
                        />
                    </v-col>
                    <v-col cols="12" sm="4">
                        <v-text-field
                            v-model="productModel.goodUnitPackageWidth"
                            label="Szerokość"
                            suffix="mm"
                            outlined
                            dense
                            :error-messages="packageWidthErrors"
                            :disabled="withoutSpecifiedCardbox || !isCustomMadeCardBoard || !isEditable || showOnly"
                        />
                    </v-col>
                    <v-col cols="12" sm="4">
                        <v-text-field
                            v-model="productModel.goodUnitPackageHeight"
                            label="Wysokość"
                            suffix="mm"
                            outlined
                            dense
                            :error-messages="packageHeightErrors"
                            :disabled="withoutSpecifiedCardbox || !isCustomMadeCardBoard || !isEditable || showOnly"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" sm="6">
                        <v-text-field
                            v-model="productModel.goodUnitPackageWeight"
                            label="Waga opakowania"
                            type="number"
                            step="0.005"
                            suffix="kg"
                            :min="0"
                            outlined
                            dense
                            :error-messages="packageWeightErrors"
                            :disabled="(isNewProduct && isProductAmountFullAssigned) || withoutSpecifiedCardbox || (!isCustomMadeCardBoard && !manualUpdate) || !isEditable || showOnly"
                        />
                    </v-col>
                    <v-col cols="12" sm="6">
                        <v-text-field
                            v-model="productModel.maxWeightCapacity"
                            label="Nośność opakowania"
                            type="number"
                            step="0.1"
                            suffix="kg"
                            :min="0"
                            outlined
                            dense
                            :error-messages="packageMaxWeightCapacityErrors"
                            :disabled="((isNewProduct && isProductAmountFullAssigned) || (withoutSpecifiedCardbox || !isCustomMadeCardBoard)) || !isEditable || showOnly"
                            @change="changeAmount"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col v-if="!manualUpdate" cols="12" sm="auto">
                        <v-checkbox
                            v-model="productModel.setManuallyAmount"
                            class="mt-2"
                            label="Ręczna edycja ilości opakowań"
                            hide-details
                            outlined
                            dense
                            :disabled="(isNewProduct && isProductAmountFullAssigned) || withoutSpecifiedCardbox || !isEditable || showOnly"
                            @change="changeAmount"
                        />
                    </v-col>
                    <v-col cols="12" sm="3">
                        <v-text-field
                            v-model="productModel.goodUnitPackageQuantity"
                            label="Ilość opakowań"
                            type="number"
                            min="0"
                            suffix="szt."
                            outlined
                            dense
                            :disabled="(isNewProduct && isProductAmountFullAssigned) || (!setAmountManually && !manualUpdate) || !isEditable || showOnly"
                            :error-messages="goodUnitPackageQuantityErrors"
                            @input="calculateGoodUnitInPackageQuantity"
                        />
                    </v-col>
                    <v-col cols="12" sm="3">
                        <v-text-field
                            v-if="manualUpdate"
                            v-model="productModel.goodUnitInPackageQuantity"
                            label="Ilość w opakowaniu"
                            type="number"
                            min="0"
                            suffix="szt."
                            outlined
                            dense
                            disabled
                            :error-messages="goodUnitInPackageQuantityErrors"
                        />
                    </v-col>
                </v-row>
                <v-alert
                    v-if="showSaveDataAlert"
                    type="error"
                    text
                    border="left"
                >
                    Dane nie są zapisane.
                </v-alert>
            </div>
            <v-divider />

            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    data-sentry-label="Zamknij"
                    :disabled="actionLoading"
                    @click="onClose"
                >
                    Anuluj
                </v-btn>
                <v-spacer />
                <v-btn
                    v-if="isEditable && !showOnly"
                    color="success"
                    text
                    :data-sentry-label="isNewProduct ? 'Dodaj' : 'Zapisz'"
                    :loading="actionLoading"
                    :disabled="(products.length === 0) || (isNewProduct && isProductAmountFullAssigned) || (formattedProducts.length === 0)"
                    @click="onSave"
                >
                    <template>
                        Zapisz
                    </template>
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import packages from '@/mixins/packages'
import { required, integer, between, minValue } from 'vuelidate/lib/validators'
import { isEmpty } from 'lodash'
import { mask } from 'vue-the-mask'
import { hasUserPermission } from '@/utils/permissionsHandler'

export default {
    name: 'ShippingPackagesConfigurationDialog',
    directives: { mask },
    mixins: [
        packages,
    ],
    props: {
        isOpen: {
            type: Boolean
        },
        isEditable: {
            type: Boolean
        },
        products: {
            type: Array,
            default: () => []
        },
        goods: {
            type: Array,
            default: () => []
        },
        productData: {
            type: Object,
            default: () => {}
        },
        isReadyToPrint: {
            type: Boolean
        },
        actionLoading: {
            type: Boolean
        },
        manualUpdate: {
            type: Boolean
        },
        action: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            productModel: {},
            selectedProduct: null,
            selectedPackage: null,
            showSaveDataAlert: false
        }
    },
    validations() {
        let rules = {
            productModel: {
                name: {
                    required,
                },
                goodUnitTargetInPackageSum: {
                    required,
                    integer,
                    between: between(1, this.wareAmountToAssign)
                },
                goodUnitPackageQuantity: {
                    required,
                    minValue: this.setAmountManually ? minValue(1) : minValue(0),
                    integer
                },
                goodUnitPackageTypeId: {
                    required
                },
                maxWeightCapacity: {
                    required,
                    minValue: minValue(0.1)
                },
                goodUnitPackageLength: {
                    required,
                    minValue: minValue(1),
                },
                goodUnitPackageWidth: {
                    required,
                    minValue: minValue(1),
                },
                goodUnitPackageHeight: {
                    required,
                    minValue: minValue(1),
                },
                goodUnitPackageWeight: {
                    required,
                    minValue: minValue(0.005),
                }
            },
        }

        if (this.withoutSpecifiedCardbox) {
            rules.productModel = {
                ...rules.productModel,
                goodUnitPackageLength: {
                    required,
                    minValue: minValue(0),
                },
                goodUnitPackageWidth: {
                    required,
                    minValue: minValue(0),
                },
                goodUnitPackageHeight: {
                    required,
                    minValue: minValue(0),
                },
                goodUnitPackageWeight: {
                    required,
                    minValue: minValue(0),
                }
            }
        }

        if (this.manualUpdate && !this.withoutSpecifiedCardbox) {
            rules.productModel = {
                ...rules.productModel,
                goodUnitPackageQuantity: {
                    required,
                    minValue: minValue(1),
                    integer,
                    between: between(1, this.goodUnitPackageQuantityToAssign)
                },
                goodUnitInPackageQuantity: {
                    required,
                    minValue:minValue(1),
                    integer,
                    between: between(1, this.goodUnitInPackageQuantityToAssign)
                },
            }
        }

        return rules
    },
    computed: {
        showOnly() {
            return this.action === 'show'
        },
        amountInfo() {
            return `${this.productModel.goodUnitTargetInPackageSum} / ${this.wareAmountToAssign}`
        },
        isNewProduct() {
            return isEmpty(this.productData)
        },
        packages() {
            return this.packageTypes.filter(item => item?.boxType?.name == 'Karton')
        },
        setAmountManually() {
            return this.productModel.setManuallyAmount
        },
        wareAmountToAssign() {
            const product = this.isNewProduct ? this.products?.find(item => item.goodName === this.productModel.name) : this.products?.find(item => item.id === this.productModel.id)
            const products = this.isNewProduct ? this.products?.filter(item => item.goodName === this.productModel.name) : this.products?.filter(item => item.availableGoodId === this.productModel.availableGoodId)
            const productsAssignedAmount = products.reduce((total, product) => {
                return total + (product.goodUnitTargetInPackageSum)
            }, 0)
            const good = this.isNewProduct ? this.goods?.find(item => item.goodName === this.productModel.name) : this.goods?.find(item => item.goodId === this.productModel.availableGoodId)

            if (good && !product && this.isNewProduct) {
                return good.availableAmount - good.assignedAmount > 0 ? good.availableAmount - good.assignedAmount : 0
            }

            if (!product || !good) return 0

            return (good.availableAmount - productsAssignedAmount + (this.productData?.goodUnitTargetInPackageSum || 0)) > 0
                ? good.availableAmount - productsAssignedAmount + (this.productData?.goodUnitTargetInPackageSum || 0) : 0
        },
        withoutSpecifiedCardbox() {
            return this.productModel.goodUnitPackageTypeId === 49
        },
        isCustomMadeCardBoard() {
            return this.productModel.goodUnitPackageTypeId === 46
        },
        isProductAmountFullAssigned() {
            return this.productModel.name !== null && this.wareAmountToAssign === 0
        },
        isAdmin() {
            return hasUserPermission('shipping_labels_admin_update')
        },
        goodUnitPackageQuantityToAssign() {
            return parseInt(this.productModel.goodUnitTargetInPackageSum) / parseInt(this.productModel.goodUnitInPackageQuantity)
        },
        goodUnitInPackageQuantityToAssign() {
            return parseInt(this.productModel.goodUnitTargetInPackageSum) / parseInt(this.productModel.goodUnitPackageQuantity)
        },
        formattedProducts() {
            if (!this.isNewProduct) {
                return this.products.map(item => ({
                    ...item,
                    name: item.goodName,
                    singleItemWeight: item.singleItemGoodWeight,
                }))
            }
            let formattedProducts = []

            this.goods?.forEach(good => {
                if (good.availableAmount - good.packedAmount > 0) {
                    formattedProducts.push({
                        name: good.goodName,
                        goodUnitTargetInPackageSum: 0,
                        goodUnitPackageQuantity: 0,
                        goodUnitInPackageQuantity: 0,
                        goodUnitPackageTypeId: null,
                        goodUnitPackageLength: null,
                        goodUnitPackageWidth:  null,
                        goodUnitPackageHeight: null,
                        goodUnitPackageWeight: null,
                        setManuallyAmount: false,
                        maxWeightCapacity: 0.0,
                        shippingLabelPositionAvailableGoodsId: good.id,
                        goodUnitDefaultInPackageSum: 0,
                        goodName: good.goodName
                    })
                }
            })

            return formattedProducts
        },

        // Walidacje
        productErrors() {
            const errors = []

            if (!this.$v.productModel.name.$dirty) return errors
            !this.$v.productModel.name.required && errors.push('Proszę wybrać produkt.')

            return errors
        },
        amountErrors() {
            const errors = []

            if (!this.$v.productModel.goodUnitTargetInPackageSum.$dirty) return errors

            if (!this.$v.productModel.goodUnitTargetInPackageSum.required) {
                errors.push('Proszę podać ilość')
            }

            if (!this.$v.productModel.goodUnitTargetInPackageSum.integer) {
                errors.push('Niepoprawny format.')
            }

            if (!this.$v.productModel.goodUnitTargetInPackageSum.required || this.wareAmountToAssign === 0 && !(this.isNewProduct && this.isProductAmountFullAssigned)) {
                errors.push('Przekroczono dostępny nakład')
            }
            if (!this.$v.productModel.goodUnitTargetInPackageSum.required || !this.$v.productModel.goodUnitTargetInPackageSum.between && !(this.isNewProduct && this.isProductAmountFullAssigned)) {
                errors.push(`Proszę podać wartość z zakresu 1-${this.wareAmountToAssign}`)
            }

            return errors
        },
        packagePackageTypeErrors() {
            const errors = []

            if (!this.$v.productModel.goodUnitPackageTypeId.$dirty) return errors
            !this.$v.productModel.goodUnitPackageTypeId.required && errors.push('Proszę wybrać rodzaj opakowania.')

            return errors
        },
        packageLengthErrors() {
            const errors = []

            if (!this.isCustomMadeCardBoard) return errors

            if (!this.$v.productModel.goodUnitPackageLength.$dirty) return errors

            if (!this.$v.productModel.goodUnitPackageLength.required || !this.$v.productModel.goodUnitPackageLength.minValue) {
                errors.push('Proszę podać długość.')
            }

            return errors
        },
        packageWidthErrors() {
            const errors = []

            if (!this.isCustomMadeCardBoard) return errors

            if (!this.$v.productModel.goodUnitPackageWidth.$dirty) return errors

            if (!this.$v.productModel.goodUnitPackageWidth.required || !this.$v.productModel.goodUnitPackageWidth.minValue) {
                errors.push('Proszę podać szerokość.')
            }

            return errors
        },
        packageHeightErrors() {
            const errors = []

            if (!this.isCustomMadeCardBoard) return errors

            if (!this.$v.productModel.goodUnitPackageHeight.$dirty) return errors

            if (!this.$v.productModel.goodUnitPackageHeight.required || !this.$v.productModel.goodUnitPackageHeight.minValue) {
                errors.push('Proszę podać wysokość.')
            }

            return errors
        },
        packageWeightErrors() {
            const errors = []

            if (!this.isCustomMadeCardBoard && !this.manualUpdate) return errors

            if (!this.$v.productModel.goodUnitPackageWeight.$dirty) return errors

            if (!this.$v.productModel.goodUnitPackageWeight.required || !this.$v.productModel.goodUnitPackageWeight.minValue) {
                errors.push('Proszę podać wagę.')
            }

            return errors
        },
        packageMaxWeightCapacityErrors() {
            const errors = []

            if (!this.withoutSpecifiedCardbox) return errors

            if (!this.$v.productModel.maxWeightCapacity.$dirty) return errors

            if (!this.$v.productModel.maxWeightCapacity.required || !this.$v.productModel.maxWeightCapacity.minValue) {
                errors.push('Proszę podać nośność.')
            }

            return errors
        },
        goodUnitPackageQuantityErrors() {
            const errors = []

            if (!this.$v.productModel.goodUnitTargetInPackageSum.$dirty) return errors

            if (!this.setAmountManually && !this.manualUpdate) return errors

            if (!this.$v.productModel.goodUnitPackageQuantity.required) {
                errors.push('Liczba opakowań jest wymagana')
            }

            if (!this.$v.productModel.goodUnitPackageQuantity.minValue || !this.$v.productModel.goodUnitPackageQuantity.integer) {
                errors.push('Proszę podać całkowitą dodatnią liczbę opakowań')
            }

            if (!this.$v.productModel.goodUnitPackageQuantity.between && this.manualUpdate) {
                errors.push('Przekroczono liczbę opakowań')
            }

            return errors
        },
        goodUnitInPackageQuantityErrors() {
            const errors = []

            if (!this.$v.productModel.goodUnitTargetInPackageSum.$dirty) return errors

            if (!this.setAmountManually && !this.manualUpdate) return errors

            if (!this.$v.productModel.goodUnitInPackageQuantity.required) {
                errors.push('Ilość w opakowaniu jest wymagana')
            }

            if (!this.$v.productModel.goodUnitInPackageQuantity.minValue || !this.$v.productModel.goodUnitInPackageQuantity.integer) {
                errors.push('Ilość w opakowaniu musi być liczbą całkowitą dodatnią')
            }
            if (!this.$v.productModel.goodUnitInPackageQuantity.between && this.manualUpdate) {
                errors.push('Przekroczono ilość w opakowaniu')
            }

            return errors
        },
    },
    watch: {
        async isOpen(value) {
            await this.getPackagesTypes()
            if (value && !isEmpty(this.productData)) {
                this.setInitialData(this.productData)
                this.changeProduct(this.selectedProduct)
                this.setGoodUnitTargetInPackageSum()
            } else {
                setTimeout(() => {
                    this.setInitialData()
                    this.$v.$reset()
                }, 500)
            }
        }
    },
    mounted() {
        this.setInitialData()
    },
    methods: {
        changeProduct(product) {
            if (this.isNewProduct) {
                this.productModel.name = product.name
                this.productModel.shippingLabelPositionAvailableGoodsId = product.shippingLabelPositionAvailableGoodsId
                this.productModel.goodName = product.goodName
            } else {
                this.productModel = {...this.productModel, ...product}
            }
            this.setGoodUnitTargetInPackageSum()
            this.$v.productModel.name.$touch()
        },
        changeAmount() {
            if (!this.productModel.setManuallyAmount && !this.isNewProduct) {
                this.productModel.goodUnitPackageQuantity = Math.ceil(this.productModel.goodUnitTargetInPackageSum * this.productModel.singleItemGoodWeight / this.productModel.maxWeightCapacity)
            }

            if (this.withoutSpecifiedCardbox || this.isNewProduct) {
                this.productModel.goodUnitPackageQuantity = 0
            } else {
                this.productModel.goodUnitPackageQuantity = Math.ceil(this.productModel.goodUnitTargetInPackageSum * this.productModel.singleItemGoodWeight / this.productModel.maxWeightCapacity)
            }
        },
        changePackageType(packageType) {
            this.productModel.goodUnitPackageTypeId = packageType.id
            this.productModel.goodUnitPackageLength = packageType.length
            this.productModel.goodUnitPackageWidth = packageType.width
            this.productModel.goodUnitPackageHeight = packageType.height
            this.productModel.goodUnitPackageWeight = packageType.weight
            this.productModel.maxWeightCapacity = packageType.maxWeightCapacity

            if (this.withoutSpecifiedCardbox || this.isNewProduct) {
                this.productModel.goodUnitPackageQuantity = 0
                this.productModel.goodUnitInPackageQuantity = 0
                this.productModel.setManuallyAmount = false
            } else {
                this.productModel.goodUnitPackageQuantity = Math.ceil(this.productModel.goodUnitTargetInPackageSum * this.productModel.singleItemGoodWeight / packageType.maxWeightCapacity)

                const remainder = this.productModel.goodUnitTargetInPackageSum % this.productModel.goodUnitPackageQuantity

                if (remainder !== 0) {
                    this.productModel.goodUnitInPackageQuantity = (this.productModel.goodUnitTargetInPackageSum / this.productModel.goodUnitPackageQuantity).toFixed(2)
                } else {
                    this.productModel.goodUnitInPackageQuantity = this.productModel.goodUnitTargetInPackageSum / this.productModel.goodUnitPackageQuantity
                }
            }
        },
        setInitialData(value = {}) {
            this.selectedProduct = !isEmpty(value) ? this.formattedProducts.find(item => item.id === value?.id) : null
            this.selectedPackage = !isEmpty(value) ? this.packageTypes.find(item => item.id == value?.goodUnitPackageTypeId) : null

            this.productModel = {
                name: value?.goodName || null,
                goodUnitTargetInPackageSum: value?.goodUnitTargetInPackageSum || 0,
                goodUnitPackageQuantity: value?.goodUnitPackageQuantity || 0,
                goodUnitInPackageQuantity: value?.goodUnitTargetInPackageSum || 0,
                goodUnitPackageTypeId: value?.goodUnitPackageTypeId || null,
                goodUnitPackageLength: value?.goodUnitPackageLength || null,
                goodUnitPackageWidth:  value?.goodUnitPackageWidth || null,
                goodUnitPackageHeight: value?.goodUnitPackageHeight || null,
                goodUnitPackageWeight: value?.goodUnitPackageWeight || null,
                setManuallyAmount: value?.setManuallyAmount || false,
                maxWeightCapacity: value?.maxWeightCapacity || 0.0,
                shippingLabelPositionAvailableGoodsId: value?.shippingLabelPositionAvailableGoodsId || null,
                goodUnitDefaultInPackageSum: value?.goodUnitDefaultInPackageSum || 0,
                goodName: value?.goodName || null,
            }
            if (!isEmpty(value)) {
                this.productModel.id = value.id
            }
        },
        setGoodUnitTargetInPackageSum() {
            const product = this.isNewProduct ? this.products?.find(item => item.goodName === this.productModel.name) : this.products?.find(item => item.id === this.productModel.id)
            const products = this.isNewProduct ? this.products?.filter(item => item.goodName === this.productModel.name) : this.products?.filter(item => item.availableGoodId === this.productModel.availableGoodId)
            const productsAssignedAmount = products.reduce((total, product) => {
                return total + (product.goodUnitTargetInPackageSum)
            }, 0)
            const good = this.isNewProduct ? this.goods?.find(item => item.goodName === this.productModel.name) : this.goods?.find(item => item.goodId === this.productModel.availableGoodId)

            if (good && !product && this.isNewProduct) {
                this.showSaveDataAlert = (good.availableAmount - good.assignedAmount) !== 0

                return good.availableAmount - good.assignedAmount > 0 ? this.productModel.goodUnitTargetInPackageSum = good.availableAmount - good.assignedAmount : 0
            }

            if (!product || !good) return 0

            this.showSaveDataAlert = (good.availableAmount - productsAssignedAmount) !== 0

            return (good.availableAmount - productsAssignedAmount + (this.productData?.goodUnitTargetInPackageSum || 0)) > 0
                ? this.productModel.goodUnitTargetInPackageSum = good.availableAmount - productsAssignedAmount + (this.productData?.goodUnitTargetInPackageSum || 0) : 0
        },
        calculateGoodUnitInPackageQuantity() {
            const remainder = this.productModel.goodUnitTargetInPackageSum % this.productModel.goodUnitPackageQuantity

            if (remainder !== 0) {
                this.productModel.goodUnitInPackageQuantity = (this.productModel.goodUnitTargetInPackageSum / this.productModel.goodUnitPackageQuantity).toFixed(2)
            } else {
                this.productModel.goodUnitInPackageQuantity = (this.productModel.goodUnitTargetInPackageSum / this.productModel.goodUnitPackageQuantity)
            }
        },
        onSave() {
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }

            let action = null

            if (this.isNewProduct) {
                action = 'createShippingLabelPackageConfiguration'
            } else {
                action = 'updateShippingLabelPackageConfiguration'
            }

            this.$emit('on-save', {
                action: action,
                product: this.productModel
            })
        },
        onClose() {
            this.$emit('on-close')
            setTimeout(() => {
                this.setInitialData()
                this.$v.$reset()
            }, 500)
        },
    }
}
</script>